import { apiClient } from '@/config/api';
import { API_BASE_URL } from '@/config/apiBaseUrl';

export type Stream = {
  id: number;
  attribute: string; // IRI
  value: string;
  timestamp: string;
};

export type StreamCollectionResponse = {
  member?: Stream[];
  'hydra:member'?: Stream[];
  totalItems?: number;
  'hydra:totalItems'?: number;
};

export const streamService = {
  async getStreamsCollectionByAttribute(
    attributeId: number,
    params?: {
      page?: number;
      itemsPerPage?: number;
      order?: { timestamp?: 'ASC' | 'DESC' };
      from?: string; // ISO date string
      to?: string; // ISO date string
    }
  ): Promise<{ items: Stream[]; totalItems: number }> {
    const { page = 1, itemsPerPage = 500, from, to } = params || {};
    const queryParams: Record<string, any> = {
      attribute: attributeId, // Use ID directly for the dedicated endpoint
      page,
      itemsPerPage,
    };

    // Use dedicated endpoint with proper date filters
    if (from) {
      queryParams.from = from;
    }
    if (to) {
      queryParams.to = to;
    }

    const res = await apiClient.get<StreamCollectionResponse>('/dashboard/streams', {
      params: queryParams,
    });
    const items = res.data.member || res.data['hydra:member'] || [];
    const totalItems = res.data.totalItems ?? res.data['hydra:totalItems'] ?? items.length;
    return { items, totalItems };
  },

  async getStreamsByAttribute(
    attributeId: number,
    params?: {
      page?: number;
      itemsPerPage?: number;
      order?: { timestamp?: 'ASC' | 'DESC' };
      from?: string; // ISO date string
      to?: string; // ISO date string
    }
  ): Promise<Stream[]> {
    const { items } = await streamService.getStreamsCollectionByAttribute(attributeId, params);
    return items;
  },

  async getAllStreamsByAttribute(
    attributeId: number,
    params?: {
      itemsPerPage?: number;
      order?: { timestamp?: 'ASC' | 'DESC' };
      from?: string; // ISO date string
      to?: string; // ISO date string
    }
  ): Promise<Stream[]> {
    const itemsPerPage = params?.itemsPerPage ?? 500;
    let page = 1;
    let totalItems: number | null = null;
    const all: Stream[] = [];

    while (true) {
      const { items, totalItems: total } = await streamService.getStreamsCollectionByAttribute(attributeId, {
        ...params,
        page,
        itemsPerPage,
      });
      if (totalItems === null) {
        totalItems = total;
      }
      all.push(...items);

      if (items.length < itemsPerPage) {
        break;
      }
      if (totalItems !== null && all.length >= totalItems) {
        break;
      }
      page += 1;
      if (page > 1000) {
        break;
      }
    }

    return all;
  },

  async exportCsv(
    projectId: number,
    attributeId: number,
    from: string,
    to: string,
    onProgress?: (progress: number) => void
  ): Promise<Blob> {
    const token = localStorage.getItem('auth_token');
    
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      // Track progress
      xhr.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const progress = (e.loaded / e.total) * 100;
          onProgress(progress);
        }
      });
      
      xhr.addEventListener('load', async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const blob = new Blob([xhr.response], { type: 'text/csv;charset=UTF-8' });
          resolve(blob);
        } else {
          // Try to parse error message from response
          let errorMessage = `Export failed (${xhr.status}): ${xhr.statusText}`;
          if (xhr.response) {
            try {
              const text = await new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = () => resolve('');
                reader.readAsText(xhr.response);
              });
              if (text) {
                try {
                  const json = JSON.parse(text);
                  errorMessage = json.message || json.detail || errorMessage;
                } catch {
                  // Not JSON, use default message
                }
              }
            } catch {
              // Could not read response
            }
          }
          reject(new Error(errorMessage));
        }
      });
      
      xhr.addEventListener('error', () => {
        reject(new Error('Network error during export'));
      });
      
      xhr.addEventListener('abort', () => {
        reject(new Error('Export cancelled'));
      });
      
      xhr.open('GET', `${API_BASE_URL}/projects/${projectId}/logements/supervision/export-csv?attributeId=${attributeId}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
      
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      
      xhr.responseType = 'blob';
      xhr.send();
    });
  },
};
